import { Option, Select } from "@deere/fuel-react";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Box, IconButton, SvgIcon, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

export interface PaginationProps {
  rowsPerPage: number;
  currentPage: number;
  totalCount?: number;
  totalPages?: number;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onFirstPageClick: () => void;
  onPreviousClick: () => void;
  onLastPageClick: () => void;
  onNextClick: () => void;
}

const PAGE_ROW_SIZE_VALUES = [10, 25, 50, 100];

const Pagination = (props: PaginationProps) => {
  const {
    rowsPerPage,
    currentPage,
    totalCount = 0,
    totalPages,
    onRowsPerPageChange,
    onFirstPageClick,
    onPreviousClick,
    onLastPageClick,
    onNextClick,
  } = props;
  const theme = useTheme();
  const { t } = useTranslation("common");
  const totalPage =
    totalPages ?? Math.max(Math.ceil(totalCount / rowsPerPage), 1);

  const renderIconButton = (
    disabled: boolean,
    onClick: () => void,
    Icon: typeof SvgIcon,
    testId: string
  ) => (
    <IconButton
      sx={{ border: `1px solid ${theme.tokens.FuelColorBorderDark}` }}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
    >
      <Icon />
    </IconButton>
  );

  return (
    <Box
      data-testid="pagination"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: theme.tokens.FuelSpacing6,
      }}
    >
      <Box display="flex" flexDirection="column" justifyContent="center">
        <Typography
          sx={{
            color: theme.tokens.FuelColorTextTertiary,
            fontSize: theme.tokens.FuelFontSizeSm,
          }}
        >
          {`${t("table.pagination.results")} `}
          {currentPage ? (currentPage - 1) * rowsPerPage + 1 : 0}
          {` - `}
          {Math.min(currentPage * rowsPerPage, totalCount)}
          {` ${t("table.pagination.results.of")} `}
          {totalCount}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={theme.tokens.FuelSpacing2}>
        {renderIconButton(
          currentPage <= 1,
          onFirstPageClick,
          FirstPageIcon,
          "first-page-button"
        )}
        {renderIconButton(
          currentPage <= 1,
          onPreviousClick,
          NavigateBeforeIcon,
          "previous-page-button"
        )}
        <Typography sx={{ color: theme.tokens.FuelColorTextTertiary }}>
          {currentPage}
          {` ${t("table.pagination.results.of")} `}
          {totalPage}
        </Typography>
        {renderIconButton(
          currentPage >= totalPage,
          onNextClick,
          NavigateNextIcon,
          "next-page-button"
        )}
        {renderIconButton(
          currentPage >= totalPage,
          onLastPageClick,
          LastPageIcon,
          "last-page-button"
        )}
      </Box>
      <Box display="flex" alignItems="center" gap={theme.tokens.FuelSpacing2}>
        <Box>
          <Typography
            sx={{
              color: theme.tokens.FuelColorTextTertiary,
              fontWeight: theme.tokens.FuelFontWeightSemibold,
              fontSize: theme.tokens.FuelFontSizeSm,
            }}
          >
            {t("table.pagination.results.resultsPerPage")}
          </Typography>
        </Box>
        <Box width="5rem">
          <Select
            value={`${rowsPerPage}`}
            onChange={(e) => {
              onRowsPerPageChange(Number(e.target.value));
            }}
            data-testid="pagination-resultPerPage-select"
          >
            {!PAGE_ROW_SIZE_VALUES.includes(rowsPerPage) && (
              <Option value={`${rowsPerPage}`}>{rowsPerPage}</Option>
            )}
            {PAGE_ROW_SIZE_VALUES.map((pageRowSize) => (
              <Option value={pageRowSize.toString()} key={pageRowSize}>
                {pageRowSize}
              </Option>
            ))}
          </Select>
        </Box>
      </Box>
    </Box>
  );
};

export default Pagination;
